import { useCallback, useEffect, useState } from 'react'
import { ConfirmDialog } from '@/components/dialog'
import { Screen, SectionHeader } from '@/components/layout'
import { Badge, Button, InlineError } from '@/components/primitives'
import { ErrorState, LoadingRegion, SkeletonList } from '@/components/states'
import type { AuthIdentity } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate, useRoute } from '@/lib/router'

type MethodsState = 'loading' | 'linking' | 'unlinking' | 'reauthRequired' | 'error' | 'ready'

const providerName = (provider: AuthIdentity['provider']) =>
  provider === 'google' ? t('signInMethods.provider.google') : t('signInMethods.provider.apple')

export function SignInMethodsScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const [state, setState] = useState<MethodsState>('loading')
  const [identities, setIdentities] = useState<AuthIdentity[]>([])
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [removeTarget, setRemoveTarget] = useState<AuthIdentity | null>(null)

  const load = useCallback(async (signal?: AbortSignal) => {
    setState('loading')
    setFailure(null)
    try {
      setIdentities(await api.auth.identities(signal))
      setState('ready')
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return
      setFailure(e instanceof ApiFailure ? e : null)
      setState('error')
    }
  }, [])

  useEffect(() => {
    const result = route.query.get('result')
    if (result === 'collision') {
      navigate('authCollision', {}, { replace: true, query: { result: 'identityOwned' } })
      return
    }
    if (result === 'error') {
      setState('error')
      navigate('signInMethods', {}, { replace: true })
      return
    }
    const controller = new AbortController()
    void load(controller.signal)
    if (result) navigate('signInMethods', {}, { replace: true })
    return () => controller.abort()
  }, [load, navigate, route.query])

  const addGoogle = () => {
    setState('linking')
    window.location.assign(api.auth.googleWebStartUrl('link', '/settings/sign-in-methods'))
  }

  const remove = () => {
    if (!removeTarget) return
    setState('unlinking')
    setFailure(null)
    void api.auth.removeIdentity(removeTarget.id).then(() => {
      setIdentities((current) => current.filter((identity) => identity.id !== removeTarget.id))
      setRemoveTarget(null)
      setState('ready')
    }).catch((e: unknown) => {
      const next = e instanceof ApiFailure ? e : null
      setRemoveTarget(null)
      setFailure(next)
      if (next?.body.code === 'reauthenticationRequired') {
        setState('reauthRequired')
        navigate('reauthenticate')
      } else {
        setState('error')
      }
    })
  }

  if (state === 'loading') {
    return (
      <Screen id="signInMethods" state={state} back={{ screen: 'settings' }}>
        <LoadingRegion><SkeletonList rows={2} /></LoadingRegion>
      </Screen>
    )
  }

  if (state === 'error' && identities.length === 0) {
    return (
      <Screen id="signInMethods" state={state} back={{ screen: 'settings' }}>
        <ErrorState titleKey="signInMethods.error.load" error={failure ?? undefined} onRetry={() => void load()} />
      </Screen>
    )
  }

  const hasGoogle = identities.some((identity) => identity.provider === 'google')
  return (
    <Screen id="signInMethods" state={state} back={{ screen: 'settings' }}>
      <div className="stack">
        {failure ? (
          <InlineError>
            {failure.body.code === 'lastSignInMethod'
              ? t('signInMethods.error.lastMethod')
              : t('signInMethods.error.unlink')}
          </InlineError>
        ) : null}

        <section>
          <SectionHeader title={t('signInMethods.section.connected')} />
          <ul className="list">
            {identities.map((identity) => {
              const name = providerName(identity.provider)
              return (
                <li key={identity.id} className="list-row list-row-static identity-row">
                  <span className="list-row-main">
                    <span className="list-row-title">{name}</span>
                    <span className="list-row-sub"><Badge tone="positive">{t('signInMethods.status.connected')}</Badge></span>
                  </span>
                  {identity.canUnlink ? (
                    <Button variant="destructive" disabled={state === 'unlinking'} onClick={() => setRemoveTarget(identity)}>
                      {state === 'unlinking' ? t('signInMethods.unlinking') : t('signInMethods.cta.remove')}
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </section>

        {!hasGoogle ? (
          <Button className="provider-button provider-button-google" block disabled={state === 'linking'} onClick={addGoogle}>
            <img className="provider-icon" src="/google-g.svg" alt="" aria-hidden="true" />
            {state === 'linking' ? t('signInMethods.linking.google') : t('signInMethods.cta.addGoogle')}
          </Button>
        ) : null}
      </div>

      {removeTarget ? (
        <ConfirmDialog
          title={t('signInMethods.remove.confirm.title', { provider: providerName(removeTarget.provider) })}
          body={t('signInMethods.remove.confirm.body', { provider: providerName(removeTarget.provider) })}
          confirmLabel={t('signInMethods.remove.confirm.cta', { provider: providerName(removeTarget.provider) })}
          busy={state === 'unlinking'}
          onCancel={() => setRemoveTarget(null)}
          onConfirm={remove}
        />
      ) : null}
    </Screen>
  )
}
